import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Company, companyAPI, CompanyListResponse, UserPermissions } from '../services/api';
import { useAuth } from './AuthContext';

interface CompanyContextType {
  // Legacy support - all client companies flattened
  companies: Company[];
  selectedCompany: Company | null;
  isLoading: boolean;
  setSelectedCompany: (company: Company | null) => void;
  refreshCompanies: () => Promise<void>;
  updateCompany: (id: number, data: Partial<Company>) => Promise<void>;
  
  // Multi-tenant support
  consultantCompanies: Company[];
  clientCompanies: Company[];
  userPermissions: UserPermissions | null;
  isConsultantUser: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [consultantCompanies, setConsultantCompanies] = useState<Company[]>([]);
  const [clientCompanies, setClientCompanies] = useState<Company[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Track last loaded user ID to prevent duplicate API calls
  const lastLoadedUserIdRef = useRef<number | null>(null);

  // Load companies when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Only refresh if user changed (not just user object reference)
      if (lastLoadedUserIdRef.current !== user.id) {
        refreshCompanies();
        lastLoadedUserIdRef.current = user.id;
      }
    } else {
      setCompanies([]);
      setConsultantCompanies([]);
      setClientCompanies([]);
      setUserPermissions(null);
      setSelectedCompany(null);
      lastLoadedUserIdRef.current = null;
    }
  }, [isAuthenticated, user]);

  // Replace the auto-select effect with a more robust version:
  useEffect(() => {
    if (companies.length === 1) {
      if (!selectedCompany || selectedCompany.id !== companies[0].id) {
        setSelectedCompany(companies[0]);
      }
    } else if (companies.length === 0 && selectedCompany) {
      setSelectedCompany(null);
    }
  }, [companies, selectedCompany]);

  const refreshCompanies = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 [CompanyContext] Loading companies for user:', user?.id);
      const companiesData: CompanyListResponse = await companyAPI.getCompanies();
      console.log('✅ [CompanyContext] Companies loaded:', companiesData);
      
      // Set multi-tenant data
      setConsultantCompanies(companiesData.consultant_companies);
      setClientCompanies(companiesData.client_companies);
      setUserPermissions(companiesData.user_permissions);
      
      // Create flattened company list for legacy compatibility
      const allClientCompanies: Company[] = [...companiesData.client_companies];
      companiesData.consultant_companies.forEach(consultant => {
        if (consultant.client_companies) {
          allClientCompanies.push(...consultant.client_companies);
        }
      });
      setCompanies(allClientCompanies);
      
    } catch (error) {
      console.error('❌ [CompanyContext] Error loading companies:', error);
      setCompanies([]);
      setConsultantCompanies([]);
      setClientCompanies([]);
      setUserPermissions(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCompany = async (id: number, data: Partial<Company>) => {
    try {
      const updatedCompany = await companyAPI.updateCompany(id, data);
      
      // Update companies list
      setCompanies(prevCompanies =>
        prevCompanies.map(company =>
          company.id === id ? { ...company, ...updatedCompany } : company
        )
      );

      // Update selected company if it's the one being updated
      if (selectedCompany?.id === id) {
        setSelectedCompany({ ...selectedCompany, ...updatedCompany });
      }
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  };

  const isConsultantUser = userPermissions?.can_manage_templates || userPermissions?.can_manage_clients || false;

  const value: CompanyContextType = {
    companies,
    selectedCompany,
    isLoading,
    setSelectedCompany,
    refreshCompanies,
    updateCompany,
    consultantCompanies,
    clientCompanies,
    userPermissions,
    isConsultantUser,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}; 